import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { ENTITY_TYPE } from "../../constants";
import {
  formatNit,
  isValidNit,
  normalizeNit,
  verificationDigit,
} from "../entities/entityLogic";
import {
  ENTITY_REQUEST_ERROR_LABELS,
  ENTITY_TYPE_LABELS,
  fieldErrorLabel,
  labelForCode,
  missingFieldsText,
} from "../entities/entityLabels";
import { getEntityConfig } from "../../services/backend/EntityConfigService";
import {
  checkEntityContact,
  registerEntity,
  sendEntityOtp,
  verifyEntityOtp,
} from "../../services/backend/EntityService";
import {
  clearRecaptcha,
  confirmPhoneCode,
  isValidPhone,
  phoneAuthErrorMessage,
  sendPhoneCode,
} from "../../services/auth/PhoneVerification";
import { NO_SMS_HINT, SMS_TEST_MODE_VISIBLE, phoneSendInfo, useResendCooldown } from "./phoneGate";
import { buildEntityData, registrationContacts } from "./entityPayload";
import { DEFAULT_PHONE_COUNTRY, composeE164, countryCodeForName } from "../../phoneCountries";
import { loginWithCustomToken } from "../../services/auth/AuthServices";
import { setUser } from "../../redux/slices/userSlice";
import PhoneNumberField from "../generic/PhoneNumberField";

// Registro público de una entidad. Tres pasos, y ninguno de ellos pide papeles.
//
// Lo que se pregunta aquí es lo mínimo con lo que se pueden crear DOS cosas: la
// entidad y la cuenta de quien la registra. Todo lo demás —nombre comercial,
// sector, dirección, sitio web, representante legal, logo y la documentación
// entera— ya se puede llenar desde Mi Entidad, con la sesión abierta, y pedirlo
// aquí solo consigue que un registro se abandone a la mitad. Un registro
// abandonado no deja entidad; uno corto sí, y esa entidad vuelve.
//
// El correo y el celular que se verifican en el paso 2 son a la vez el contacto
// de la entidad y las credenciales de la persona: con ese correo entra a la
// aplicación. Por eso las dos puertas de verificación siguen siendo
// obligatorias aunque el resto del formulario se haya encogido.

const RECAPTCHA_ID = "recaptcha-entity-registro";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
  nit: "",
  legalName: "",
  entityType: "",
  country: "Colombia",
  email: "",
  phone: "",
};

// Quien registra. Con esto se crea su usuario en `users`: sin nombres,
// apellidos y documento la cuenta nace incompleta y ProtectedRoute la manda a
// /profile en el primer clic, que es exactamente la bienvenida que no queremos.
const emptyFounder = {
  name: "",
  lastName: "",
  nuip: "",
  position: "",
  isLegalRep: false,
};

const errorText = (code) =>
  code ? labelForCode(ENTITY_REQUEST_ERROR_LABELS, code, "No fue posible completar la operación.") : null;

export default function EntityRegistrationForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cfg, setCfg] = useState({ smsTestMode: false });
  const [form, setForm] = useState(emptyForm);
  const [founder, setFounder] = useState(emptyFounder);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Qué campo quedó mal, no sólo que algo quedó mal. fieldErrors[campo] = CÓDIGO,
  // los mismos códigos que devuelve la función, así que el helperText de un campo
  // dice lo mismo lo haya rechazado el formulario o el servidor.
  const [fieldErrors, setFieldErrors] = useState({});

  // Verificación de correo (OTP del servidor).
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(null);

  // Verificación de celular (Firebase Phone Auth en el cliente).
  const [phoneConfirmation, setPhoneConfirmation] = useState(null);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneInfo, setPhoneInfo] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneToken, setPhoneToken] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  // El país del celular arranca en el de la entidad —hoy siempre Colombia, que
  // es lo que trae emptyForm— y a partir de ahí lo manda quien llena el
  // formulario. Es la única forma de que un número extranjero se guarde bien:
  // sin el selector, toE164 le encajaba +57 a cualquier cosa sin "+".
  const [phoneCountry, setPhoneCountry] = useState(
    () => countryCodeForName(emptyForm.country) || DEFAULT_PHONE_COUNTRY
  );
  const phoneCooldown = useResendCooldown();

  useEffect(() => {
    getEntityConfig().then(setCfg);
    return () => clearRecaptcha();
  }, []);

  const steps = [
    { key: "empresa", label: "Tu empresa" },
    { key: "persona", label: "Tus datos" },
    { key: "revision", label: "Revisión" },
  ];

  // Corregir un campo borra su marca: dejarla puesta mientras se teclea la
  // corrección convierte el aviso en ruido.
  const clearFieldError = (...keys) =>
    setFieldErrors((prev) => {
      if (!keys.some((k) => prev[k])) return prev;
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });

  const set = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    clearFieldError(key);
  };

  const setPerson = (key, errorKey) => (e) => {
    const { value } = e.target;
    setFounder((p) => ({ ...p, [key]: value }));
    clearFieldError(errorKey);
  };

  const fieldText = (key, fallback) => fieldErrorLabel(fieldErrors[key]) || fallback;

  // Cambiar el correo o el celular después de verificarlo invalida la
  // verificación: lo que se probó fue el valor anterior.
  const onEmailChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, email: value }));
    clearFieldError("email");
    if (emailVerified && value.trim() !== verifiedEmail) {
      setEmailVerified(false); setOtpSent(false); setOtpCode(""); setOtpInfo(null);
    }
  };
  // Cambiar el número O EL PAÍS después de verificarlo invalida la
  // verificación: lo que se probó fue el número completo, indicativo incluido.
  const onPhoneChange = ({ countryCode, national }) => {
    setPhoneCountry(countryCode);
    setForm((f) => ({ ...f, phone: national }));
    clearFieldError("phone");
    if (phoneVerified && composeE164(countryCode, national) !== verifiedPhone) {
      setPhoneVerified(false); setPhoneConfirmation(null); setPhoneCode("");
      setPhoneInfo(null); setPhoneToken(null); phoneCooldown.reset();
    }
  };

  const handleSendOtp = async () => {
    const dest = form.email.trim();
    if (!EMAIL_RE.test(dest)) { setError("Ingresa un correo electrónico válido."); return; }
    setSubmitting(true); setError(null);
    // Falla rápido, ANTES de gastar un correo, si ya es de otra entidad o si ya
    // tiene cuenta en la aplicación. Lo segundo importa más de lo que parece: sin
    // ese aviso, quien usa el correo con el que ya entra a la aplicación —el caso
    // típico de un funcionario registrando su propia empresa— verificaba correo y
    // celular, registraba la entidad, y la cuenta no se creaba. Quedaba una
    // empresa registrada a la que nadie podía entrar.
    const chk = await checkEntityContact({ nit: form.nit, email: dest });
    if (chk.emailInUse || chk.emailHasAccount) {
      setSubmitting(false);
      setFieldErrors((p) => ({ ...p, email: chk.emailInUse ? "EMAIL_IN_USE" : "EMAIL_HAS_ACCOUNT" }));
      setError(errorText(chk.emailInUse ? "EMAIL_IN_USE" : "EMAIL_HAS_ACCOUNT"));
      return;
    }
    const { ok, json } = await sendEntityOtp({ recipient: dest });
    setSubmitting(false);
    if (!ok) { setError(errorText(json?.error) || "No fue posible enviar el código."); return; }
    setOtpSent(true);
    setOtpInfo(`Enviamos un código a ${dest}. Vence en ${json?.expiresInMinutes || 10} minutos.`);
  };

  const handleVerifyOtp = async () => {
    setSubmitting(true); setError(null);
    const { ok, json } = await verifyEntityOtp({ recipient: form.email.trim(), code: otpCode.trim() });
    setSubmitting(false);
    if (!ok) { setError(errorText(json?.error) || "Código incorrecto."); return; }
    setEmailVerified(true);
    setVerifiedEmail(form.email.trim());
    setOtpInfo(null);
  };

  const handleSendPhoneCode = async () => {
    const e164 = composeE164(phoneCountry, form.phone);
    if (!isValidPhone(e164)) { setPhoneError("Ingresa un número de celular válido."); return; }
    setPhoneBusy(true); setPhoneError(null);
    try {
      const chk = await checkEntityContact({ nit: form.nit, phone: e164 });
      if (chk.phoneInUse) { setPhoneError(errorText("PHONE_IN_USE")); setPhoneBusy(false); return; }
      const confirmation = await sendPhoneCode(e164, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setPhoneConfirmation(confirmation);
      setPhoneInfo(phoneSendInfo(e164, !!cfg.smsTestMode));
      phoneCooldown.start();
    } catch (e) {
      setPhoneError(phoneAuthErrorMessage(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneConfirmation) return;
    setPhoneBusy(true); setPhoneError(null);
    try {
      const { idToken } = await confirmPhoneCode(phoneConfirmation, phoneCode);
      setPhoneToken(idToken);
      setPhoneVerified(true);
      setVerifiedPhone(composeE164(phoneCountry, form.phone));
      setPhoneInfo(null);
      setPhoneConfirmation(null);
      phoneCooldown.reset();
    } catch (e) {
      setPhoneError(phoneAuthErrorMessage(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleNit = async () => {
    // El NIT es el id del documento, así que un duplicado no se puede resolver
    // más tarde: se avisa aquí, antes de que la entidad llene todo el formulario.
    if (!isValidNit(form.nit)) {
      setFieldErrors((p) => ({ ...p, nit: "NIT_INVALID" }));
      setError(errorText("NIT_INVALID"));
      return false;
    }
    const chk = await checkEntityContact({ nit: form.nit });
    if (chk.nitInUse) {
      setFieldErrors((p) => ({ ...p, nit: "NIT_ALREADY_REGISTERED" }));
      setError(errorText("NIT_ALREADY_REGISTERED"));
      return false;
    }
    return true;
  };

  // Qué le falta a un paso, CAMPO POR CAMPO. Devuelve { campo: CÓDIGO } en vez de
  // un booleano: un «completa los campos obligatorios» obliga a quien llena el
  // formulario a adivinar cuál de los campos es.
  const stepErrors = (key) => {
    const errs = {};

    if (key === "empresa") {
      if (!isValidNit(form.nit)) errs.nit = "NIT_INVALID";
      if (form.legalName.trim().length < 3) errs.legalName = "LEGAL_NAME_REQUIRED";
      if (!form.entityType) errs.entityType = "INVALID_ENTITY_TYPE";
    }

    if (key === "persona") {
      // Estos tres no son adorno: con ellos se crea la cuenta. Sin apellidos o
      // sin documento el usuario queda incompleto y la aplicación lo devuelve a
      // /profile apenas entra.
      if (!founder.name.trim()) errs.founderName = "REQUIRED";
      if (!founder.lastName.trim()) errs.founderLastName = "REQUIRED";
      if (!founder.nuip.trim()) errs.founderNuip = "REQUIRED";
      if (!emailVerified || form.email.trim() !== verifiedEmail) errs.email = "EMAIL_NOT_VERIFIED";
      if (!phoneVerified || composeE164(phoneCountry, form.phone) !== verifiedPhone) {
        errs.phone = "PHONE_NOT_VERIFIED";
      }
      if (!checked) errs.terms = "TERMS_REQUIRED";
    }

    return errs;
  };

  const next = async () => {
    setError(null);
    // Primero lo que se puede ver sin preguntarle a nadie: así el aviso nombra
    // TODO lo que falta de una vez, y el NIT sólo se va a consultar al servidor
    // cuando el resto del paso ya esté bien.
    const errs = stepErrors(steps[step].key);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(`Falta completar: ${missingFieldsText(errs)}.`);
      return;
    }
    if (steps[step].key === "empresa" && !(await handleNit())) return;
    setFieldErrors({});
    setStep((s) => s + 1);
  };

  const fullName = `${founder.name.trim()} ${founder.lastName.trim()}`.trim();

  const handleSubmit = async () => {
    setSubmitting(true); setError(null);
    const nit = normalizeNit(form.nit);
    const { ok, json } = await registerEntity({
      data: {
        ...buildEntityData({
          ...form,
          // Marcar la casilla es lo único que hay del representante legal en
          // este formulario. Es el caso normal —quien registra la empresa suele
          // serlo— y ahorra un paso entero; quien no lo sea lo registra después
          // desde Mi Entidad, que es donde vive el resto de la ficha.
          ...(founder.isLegalRep
            ? { legalRepName: fullName, legalRepIdNumber: founder.nuip.trim() }
            : {}),
        }),
        ...registrationContacts({
          email: form.email,
          phone: phoneE164,
          phoneCountry,
          name: fullName,
        }),
      },
      founder: {
        name: founder.name.trim(),
        lastName: founder.lastName.trim(),
        nuip: founder.nuip.trim(),
        position: founder.position.trim(),
      },
      idempotencyKey: `entity:${nit}:registro`,
      phoneToken,
    });
    setSubmitting(false);
    if (!ok) {
      // La función también contesta campo por campo (VALIDATION → errors). Se
      // pintan igual que los del formulario: quien llenó el registro no tiene por
      // qué saber cuál de los dos lo rechazó.
      const serverErrors = json?.errors || json?.body?.errors;
      if (serverErrors && typeof serverErrors === "object" && Object.keys(serverErrors).length) {
        setFieldErrors(serverErrors);
        setError(`Revisa: ${missingFieldsText(serverErrors)}.`);
        return;
      }
      setError(errorText(json?.error) || "No fue posible completar el registro. Intenta de nuevo.");
      return;
    }
    // intakeEntity crea la cuenta en la misma petición y devuelve un token: con
    // las dos verificaciones recién hechas, volver a pedir que inicien sesión
    // sería pedirles que prueben otra vez lo que acaban de probar. Caen en
    // /home, con la baldosa de Mi Entidad esperándolos.
    //
    // Si no vuelve token es que la cuenta no se pudo crear (el correo ya tenía
    // una, o Auth falló). La entidad QUEDÓ registrada igual, así que se les
    // manda al login en vez de dejarlos mirando un spinner.
    if (json?.customToken) {
      const session = await loginWithCustomToken(json.customToken, true);
      if (session?.user) {
        dispatch(setUser(session.user));
        // A Mi Entidad y no al Home: la cuenta nace con lo mínimo y lo que
        // sigue —documentos, dirección, sector, representante legal— se
        // completa ahí. El Home tiene sus baldosas esperando cuando quiera.
        navigate("/my-entity", { state: { justRegistered: true } });
        return;
      }
    }
    navigate("/login", { state: { justRegistered: true, email: form.email.trim() } });
  };

  const dv = verificationDigit(form.nit);
  // El número completo, tal como se va a guardar y a verificar.
  const phoneE164 = composeE164(phoneCountry, form.phone);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Registro de entidad</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Registra tu empresa o entidad para poder suscribir convenios con la Dirección de
        Extensión Académica. Son tres pasos: los documentos y el resto de los datos los
        cargas después, ya dentro de la aplicación.
      </Typography>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((s) => <Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        {steps[step].key === "empresa" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                Sólo lo indispensable para identificar la entidad. La dirección, el sector,
                el sitio web y el logo se completan luego desde <strong>Mi Entidad</strong>.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="NIT (sin dígito de verificación)"
                value={form.nit} onChange={set("nit")}
                error={!!fieldErrors.nit}
                helperText={fieldText(
                  "nit",
                  form.nit ? `Se registrará como ${formatNit(form.nit)}` : "Sólo los dígitos, sin puntos ni guion."
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth disabled label="Dígito de verificación"
                value={dv === null ? "" : dv}
                helperText="Lo calculamos nosotros con el algoritmo de la DIAN." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Razón social"
                value={form.legalName} onChange={set("legalName")}
                error={!!fieldErrors.legalName}
                helperText={fieldText("legalName")} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth required label="Naturaleza jurídica"
                value={form.entityType} onChange={set("entityType")}
                error={!!fieldErrors.entityType}
                helperText={fieldText("entityType", "Determina qué documentos se te pedirán después.")}>
                {Object.values(ENTITY_TYPE).map((t) => (
                  <MenuItem key={t} value={t}>{ENTITY_TYPE_LABELS[t]}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        {steps[step].key === "persona" && (
          <Stack spacing={3}>
            <Alert severity="info">
              Con estos datos creamos <strong>tu cuenta</strong>. El correo que verifiques aquí
              es con el que vas a entrar a la aplicación, y también queda como el correo de
              contacto de la entidad.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Nombres"
                  value={founder.name} onChange={setPerson("name", "founderName")}
                  error={!!fieldErrors.founderName}
                  helperText={fieldText("founderName")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Apellidos"
                  value={founder.lastName} onChange={setPerson("lastName", "founderLastName")}
                  error={!!fieldErrors.founderLastName}
                  helperText={fieldText("founderLastName")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Número de documento"
                  value={founder.nuip} onChange={setPerson("nuip", "founderNuip")}
                  error={!!fieldErrors.founderNuip}
                  helperText={fieldText("founderNuip")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Cargo en la empresa (opcional)"
                  value={founder.position} onChange={setPerson("position", "founderPosition")} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={founder.isLegalRep}
                      onChange={(e) => setFounder((p) => ({ ...p, isLegalRep: e.target.checked }))}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Soy el representante legal de la empresa
                    </Typography>
                  }
                />
                {!founder.isLegalRep && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Si no lo eres, registra al representante legal más tarde desde
                    Mi Entidad → Datos. No hace falta para registrarte.
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider />

            {/* Correo */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Correo electrónico</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
                <TextField fullWidth required type="email" label="Tu correo"
                  value={form.email} onChange={onEmailChange}
                  disabled={emailVerified}
                  error={!!fieldErrors.email}
                  helperText={fieldText(
                    "email",
                    "Con este correo entras a la aplicación y recibes los avisos de la entidad."
                  )} />
                {!emailVerified && (
                  <Button variant="outlined" disabled={submitting || !form.email.trim()}
                    onClick={handleSendOtp} sx={{ mt: { sm: 1 }, whiteSpace: "nowrap" }}>
                    {otpSent ? "Reenviar código" : "Enviar código"}
                  </Button>
                )}
              </Stack>
              {emailVerified && (
                <Chip sx={{ mt: 1 }} color="success" icon={<CheckCircleIcon />} label="Correo verificado" />
              )}
              {otpInfo && <Alert severity="info" sx={{ mt: 1 }}>{otpInfo}</Alert>}
              {otpSent && !emailVerified && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField label="Código" value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)} inputProps={{ maxLength: 6 }} />
                  <Button variant="contained" disabled={submitting || otpCode.trim().length < 6}
                    onClick={handleVerifyOtp}>Verificar</Button>
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Celular */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Celular</Typography>
              {SMS_TEST_MODE_VISIBLE && cfg.smsTestMode && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Modo de prueba de SMS activo: no se envían mensajes reales.
                </Alert>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
                <Box sx={{ flexGrow: 1, width: "100%" }}>
                  <PhoneNumberField
                    required
                    label="Tu celular"
                    countryCode={phoneCountry}
                    national={form.phone}
                    onChange={onPhoneChange}
                    disabled={phoneVerified}
                    error={!!fieldErrors.phone}
                    helperText={fieldText(
                      "phone",
                      "Debe ser un celular: a un fijo no le podemos enviar el código. Los fijos se agregan después."
                    )}
                  />
                </Box>
                {!phoneVerified && (
                  <Button variant="outlined"
                    disabled={phoneBusy || !form.phone.trim() || phoneCooldown.seconds > 0}
                    onClick={handleSendPhoneCode} sx={{ mt: { sm: 1 }, whiteSpace: "nowrap" }}>
                    {phoneCooldown.seconds > 0 ? `Reenviar (${phoneCooldown.seconds}s)` : "Enviar código"}
                  </Button>
                )}
              </Stack>
              {phoneVerified && (
                <Chip sx={{ mt: 1 }} color="success" icon={<CheckCircleIcon />} label="Celular verificado" />
              )}
              {phoneInfo && <Alert severity="info" sx={{ mt: 1 }}>{phoneInfo}</Alert>}
              {phoneError && <Alert severity="error" sx={{ mt: 1 }}>{phoneError}</Alert>}
              {phoneConfirmation && !phoneVerified && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField label="Código" value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)} inputProps={{ maxLength: 6 }} />
                    <Button variant="contained" disabled={phoneBusy || phoneCode.trim().length < 6}
                      onClick={handleVerifyPhoneCode}>Verificar</Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{NO_SMS_HINT}</Typography>
                </>
              )}
              <div id={RECAPTCHA_ID} />
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => { setChecked(e.target.checked); clearFieldError("terms"); }}
                  color={fieldErrors.terms ? "error" : "primary"}
                />
              }
              label={
                <Typography variant="body2" color={fieldErrors.terms ? "error" : undefined}>
                  Declaro que la información es veraz y autorizo el tratamiento de los datos
                  de la entidad conforme a la política de tratamiento de datos personales.
                </Typography>
              }
            />
          </Stack>
        )}

        {steps[step].key === "revision" && (
          <Stack spacing={2}>
            <Alert severity="info">
              Al enviar quedas dentro de la aplicación con tu cuenta creada. Desde ahí cargas
              los documentos y completas los datos de la entidad; el equipo de Extensión la
              aprueba cuando la documentación esté completa.
            </Alert>
            <Typography variant="subtitle2" color="text.secondary">La entidad</Typography>
            <ReviewRow label="NIT" value={formatNit(form.nit)} />
            <ReviewRow label="Razón social" value={form.legalName} />
            <ReviewRow label="Naturaleza jurídica" value={ENTITY_TYPE_LABELS[form.entityType]} />
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Tu cuenta</Typography>
            <ReviewRow label="Nombre" value={fullName} />
            <ReviewRow label="Documento" value={founder.nuip} />
            <ReviewRow label="Cargo" value={founder.position || "—"} />
            <ReviewRow
              label="Representante legal"
              value={founder.isLegalRep ? "Sí" : "Se registra después"}
            />
            <ReviewRow label="Correo" value={`${form.email} (verificado)`} />
            <ReviewRow label="Celular" value={`${phoneE164 || form.phone} (verificado)`} />
          </Stack>
        )}

        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 3 }}>
          <Button disabled={step === 0 || submitting} onClick={() => setStep((s) => s - 1)}>
            Atrás
          </Button>
          {step < steps.length - 1 ? (
            <Button variant="contained" onClick={next} disabled={submitting}>Continuar</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Enviando…" : "Crear mi cuenta y registrar"}
            </Button>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

function ReviewRow({ label, value }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>{value || "—"}</Typography>
    </Stack>
  );
}
